import { Component, inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { OrderService } from '../../../services/order.service';
import { FNFResponse } from '../../../models/response';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment-timezone';
import { NgxSpinnerService } from 'ngx-spinner';
import { RestaurantService } from '../../../services/restaurant.service';

@Component({
  selector: 'app-order-payment',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './order-payment.component.html',
  styleUrl: './order-payment.component.css',
})
export class OrderPaymentComponent implements AfterViewInit {
  readonly orderId = inject(ActivatedRoute).snapshot.params['order_id'] as string;
  orderService = inject(OrderService);
  restaurantService = inject(RestaurantService);
  toastr = inject(ToastrService);
  router = inject(Router);
  ngxSpinnerService = inject(NgxSpinnerService);
  @ViewChild('paymentsContainer', { static: false }) paymentsContainer!: ElementRef<HTMLDivElement>;
  order: any;
  payments: any[] = [];
  expandedRefunds: { [key: string]: boolean } = {};
  restaurantTimezone: any;
  scrollAmount: number = 400; // Amount to scroll per click
  canScrollLeftState: boolean = false;
  canScrollRightState: boolean = true;

  constructor() {
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.restaurantTimezone = restaurant?.time_zone;
    });
  }

  ngOnInit() {
    this.ngxSpinnerService.show();
    this.orderService.getOrderById(this.orderId).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.order = response.data;
        // Initialize payments from order data
        // Assuming payments are in order.payments or order.payment_history
        this.payments = this.order?.payment_method?.payments || this.order.payment_history || [];
        // Initialize expanded state for refunds
        this.payments.forEach((payment, index) => {
          if (payment.refunds && payment.refunds.length > 0) {
            this.expandedRefunds[`payment-${index}-refund-0`] = true; // Expand first refund by default
          }
        });
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  ngAfterViewInit(): void {
    // Add scroll event listener to update button states
    if (this.paymentsContainer) {
      this.paymentsContainer.nativeElement.addEventListener('scroll', () => {
        this.updateScrollButtons();
      });
      // Initial check
      setTimeout(() => {
        this.updateScrollButtons();
      }, 100);
    }
  }

  updateScrollButtons(): void {
    if (!this.paymentsContainer) return;
    const element = this.paymentsContainer.nativeElement;
    this.canScrollLeftState = element.scrollLeft > 0;
    this.canScrollRightState = element.scrollLeft < element.scrollWidth - element.clientWidth - 1;
  }

  getTotalPayment(): number {
    return this.payments.reduce((sum, payment) => sum + (parseFloat(payment.total) || 0), 0);
  }

  getTotalRefund(): number {
    return this.payments.reduce((sum, payment) => {
      const refundTotal = (payment.refunds || []).reduce(
        (refundSum: number, refund: any) => refundSum + (parseFloat(refund.refund_amount) || 0),
        0,
      );
      return sum + refundTotal;
    }, 0);
  }

  getTotalRefundForPayment(payment: any): number {
    return (payment.refunds || []).reduce(
      (sum: number, refund: any) => sum + (parseFloat(refund.refund_amount) || 0),
      0,
    );
  }

  getPaymentsWithRefundsCount(): number {
    return this.payments.filter((p) => p.refunds && p.refunds.length > 0).length;
  }

  getAbsoluteRefundTotal(): number {
    return Math.abs(this.getTotalRefund());
  }

  parseFloat(value: any): number {
    return parseFloat(value) || 0;
  }

  toggleRefund(paymentIndex: number, refundIndex: number): void {
    const key = `payment-${paymentIndex}-refund-${refundIndex}`;
    this.expandedRefunds[key] = !this.expandedRefunds[key];
  }

  isRefundExpanded(paymentIndex: number, refundIndex: number): boolean {
    return this.expandedRefunds[`payment-${paymentIndex}-refund-${refundIndex}`] || false;
  }

  formatDate(dateValue: any): string {
    const parsed = this.parseDateValue(dateValue);
    if (!parsed) return '-';
    if (this.restaurantTimezone) {
      return parsed.tz(this.restaurantTimezone).format('DD-MMM-YYYY HH:mm');
    }
    return parsed.format('DD-MMM-YYYY HH:mm');
  }

  formatDateTime(dateValue: any): string {
    const parsed = this.parseDateValue(dateValue);
    if (!parsed) return '-';
    if (this.restaurantTimezone) {
      return parsed.tz(this.restaurantTimezone).format('DD MMM YYYY • HH:mm');
    }
    return parsed.format('DD MMM YYYY • HH:mm');
  }

  private parseDateValue(dateValue: any): moment.Moment | null {
    if (dateValue === null || dateValue === undefined || dateValue === '') {
      return null;
    }

    if (
      typeof dateValue === 'number' ||
      (typeof dateValue === 'string' && /^\d+$/.test(dateValue))
    ) {
      const numericValue = Number(dateValue);
      // API returns Unix timestamps in seconds; JS Date/moment expect milliseconds.
      return numericValue < 1e12 ? moment.unix(numericValue) : moment(numericValue);
    }

    const parsed = moment(dateValue);
    return parsed.isValid() ? parsed : null;
  }

  getCustomerInitials(customer: any): string {
    if (!customer) return '';
    const firstName = customer || '';
    const lastName = customer.last_name || '';
    return (firstName.charAt(0) + (lastName ? lastName.charAt(0) : '')).toUpperCase();
  }

  navigateBack(): void {
    this.router.navigate(['/main/order']);
  }

  scrollLeft(): void {
    if (this.paymentsContainer) {
      this.paymentsContainer.nativeElement.scrollBy({
        left: -this.scrollAmount,
        behavior: 'smooth',
      });
      // Update button states after scroll
      setTimeout(() => this.updateScrollButtons(), 100);
    }
  }

  scrollRight(): void {
    if (this.paymentsContainer) {
      this.paymentsContainer.nativeElement.scrollBy({
        left: this.scrollAmount,
        behavior: 'smooth',
      });
      // Update button states after scroll
      setTimeout(() => this.updateScrollButtons(), 100);
    }
  }

  canScrollLeft(): boolean {
    return this.canScrollLeftState;
  }

  canScrollRight(): boolean {
    return this.canScrollRightState;
  }

  getCustomerName(userId: number): string {
    return this.getPaymentCustomer(userId)?.first_name || '';
  }

  // A payment only carries user_id; the payer's name and photo live on order.customers.
  getPaymentCustomer(userId: number | string): any {
    return this.order?.customers?.find((customer: any) => customer.user_id === userId);
  }

  // Payer photo when one is set and it loads; otherwise the initials badge.
  private failedCustomerImages = new Set<string>();

  hasCustomerImage(customer: any): boolean {
    const url = customer?.img_path?.trim();
    return !!url && !this.failedCustomerImages.has(url);
  }

  onCustomerImageError(customer: any): void {
    this.failedCustomerImages.add(customer.img_path.trim());
  }
}
